import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useSession } from '../../state/useSession';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import { Button, Screen, TextField } from '../../ui';
import { failureCode, signUpRefusal } from './plainWords';
import { ExampleSite, IdeaQuote, ideaFrom } from './JourneyIdea';
import type { ConfirmState } from './Confirm';
import ReportProblem from '../../shell/ReportProblem';
import { funnelStep, useFunnelStep, type FunnelProps } from '../../analytics/funnel';
import { inAppBrowser } from '../../analytics/device';
import { authorizeUrl, markLeavingForSignIn, socialProviders, type Provider } from '../../api/oauth';
import { stashDraft } from '../../state/draftStash';
import { useDraft } from '../../state/draft';
import { rememberPendingCode } from '../../state/pendingCode';
import { openInBrowserUrl } from '../../platform/openInBrowser';
import { deviceProps } from '../../analytics/device';
import { leaveFor } from '../../platform/leave';

/** In-app browsers by the name people know them by, for the line that explains Google's absence. */
const IN_APP_NAME = { facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok' } as const;

/**
 * Enough of an address to send a code to: something@something.something, no spaces. Loose on
 * purpose — the code proves the address, and a stricter pattern turns away real ones.
 */
const looksLikeEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * `card`: the same sign-up, shown as a card over the loading view after Create (2026-10-04, the
 * preview-first journey): the app keeps getting ready while they type their email, and the code
 * brings them back to `card.from` (the plan's capabilities).
 */
export default function SignUp({ card }: { card?: { from: string } } = {}) {
  const { startEmail } = useSession();
  const navigate = useNavigate();
  /*
   * Where the person was going when an account was asked of them — the plan they were
   * building, an invite they opened. Carried through confirmation to the sign-in that follows
   * it, which is where they are finally returned: dropped here, "sign in to keep going"
   * delivered everybody without an account (most of whom it was said to) to the home page.
   */
  const arrived = useLocation().state as { from?: string; fromBuild?: boolean } | null;
  const from = card?.from ?? arrived?.from;
  // Sent here by the plan's Build button (Plan.tsx askForAccount), the main way in since 2026-10-03.
  const fromBuild = !card && arrived?.fromBuild === true;
  const idea = card ? null : ideaFrom(from);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'email' | Provider | null>(null);
  /** Said only once they have left the field: "new@user" is fine while it is being typed. */
  const [emailLeft, setEmailLeft] = useState(false);

  /*
   * The ways in, in the handoff's order: Apple (most visitors are on iPhones), Google, then email.
   * Google refuses its sign-in inside an app's own browser (`disallowed_useragent`) — where most ad
   * clicks open — so there it is not offered, and the line under the buttons says why.
   */
  const providers = socialProviders();
  const inApp = typeof navigator === 'undefined' ? 'none' : inAppBrowser(navigator.userAgent);
  const showApple = providers.includes('apple');
  const showGoogle = providers.includes('google') && inApp === 'none';
  const googleHidden = providers.includes('google') && inApp !== 'none';
  const methodsShown = [showApple && 'apple', showGoogle && 'google', 'email'].filter(Boolean).join('_') as NonNullable<FunnelProps['methods_shown']>;

  useFunnelStep('signup_viewed', true, { from_build: fromBuild, methods_shown: methodsShown });
  /*
   * Leaving without an account, and how fast: "were these misclicks?" — which nothing answered
   * when every ad visitor met this page first and every one left (2026-10-02). On pagehide only (a
   * closed tab, the app switched away), with keepalive; created accounts never send it.
   */
  const created = useRef(false);
  useEffect(() => {
    const opened = Date.now();
    const onHide = (): void => {
      if (created.current) return;
      const s = (Date.now() - opened) / 1000;
      const bucket = s < 3 ? '<3' : s < 10 ? '3-10' : s < 30 ? '10-30' : '30+';
      funnelStep('signup_left', { seconds_on_page: bucket, from_build: fromBuild }, { now: true, pixel: false });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [fromBuild]);

  const emailOk = looksLikeEmail(email);

  /**
   * Off to Google or Apple, and back to /auth/callback. Their plan goes into this tab's storage
   * first — the page is about to be replaced — and is claimed by the account that comes back
   * (draftStash.ts, the same trip a checkout takes).
   */
  async function continueWith(provider: Provider): Promise<void> {
    if (busy) return;
    setError(null);
    setBusy(provider);
    funnelStep('signup_method_chosen', { method: provider }, { now: true });
    created.current = true; // leaving on purpose: not a `signup_left`
    const name = provider === 'apple' ? 'Apple' : 'Google';
    try {
      // Durable: the return may land in another view of an app's browser, without this tab's store.
      await stashDraft('', { durable: true });
      markLeavingForSignIn();
      leaveFor(await authorizeUrl(provider, from));
    } catch {
      markLeavingForSignIn(false);
      created.current = false;
      funnelStep('signup_failed', { method: provider, code: 'start' });
      setError(`Could not open ${name} just now. Try again, or continue with your email.`);
      setBusy(null);
      return;
    }
    /*
     * "Apple tapped, then nothing" (2026-10-06): inside Facebook's browser the page did not leave —
     * the person sat on the card for 22 seconds and went. If this page is still here and in front a
     * few seconds on, or comes back to the front without having left, the trip did not happen or did
     * not finish: say so, give the buttons back, and log why.
     */
    let left = false;
    const onHide = (): void => {
      left = true;
    };
    window.addEventListener('pagehide', onHide);
    const giveBack = (code: 'did_not_open' | 'came_back'): void => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(timer);
      if (left) return;
      markLeavingForSignIn(false);
      created.current = false;
      funnelStep('signup_failed', { method: provider, code }, { now: true });
      setError(
        code === 'did_not_open'
          ? `${name} didn’t open here. Try again, or continue with your email — it works in this browser.`
          : `${name} sign-in didn’t finish. Try again, or continue with your email.`,
      );
      setBusy(null);
    };
    let wasHidden = false;
    const onVisible = (): void => {
      if (document.visibilityState === 'hidden') wasHidden = true;
      else if (wasHidden) giveBack('came_back');
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setTimeout(() => {
      if (document.visibilityState === 'visible' && !wasHidden) giveBack('did_not_open');
    }, 10_000);
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    // Enter on the keyboard submits too, and the button's disabled state does not stop that.
    if (!emailOk || busy) return;
    setError(null);
    setBusy('email');
    funnelStep('signup_method_chosen', { method: 'email' });
    try {
      /*
       * One field, no password (easy-signup, 2026-10-04). A new email gets an account and a
       * confirmation code; one that has an account gets a sign-in code. Pressing Continue IS the
       * acceptance of the terms said directly above it, stamped as custom:terms_accepted_at.
       */
      const started = await startEmail(email.trim());
      created.current = true;
      /*
       * For the trip to the mail app (handoff §4): Facebook's browser may reload the page on the
       * way back, so their plan and the code screen's state are put where a reload finds them.
       */
      if (useDraft.getState().description.trim()) await stashDraft('', { durable: true });
      rememberPendingCode({
        email: email.trim(),
        mode: started.mode,
        ...(started.mode === 'signin' ? { session: started.session } : {}),
        ...(from ? { from } : {}),
        sentAt: Date.now(),
      });
      // No props: never the email or the idea.
      if (started.mode === 'signup') funnelStep('signup_submitted');
      // sentAt: the code just went out, so the code screen's Resend waits from now.
      const state: ConfirmState = {
        email: email.trim(),
        sentAt: Date.now(),
        mode: started.mode,
        ...(started.mode === 'signin' ? { session: started.session } : {}),
        ...(from ? { from } : {}),
      };
      navigate('/confirm', { state });
    } catch (err) {
      funnelStep('signup_failed', { method: 'email', code: failureCode(err) });
      setError(signUpRefusal(err));
    } finally {
      setBusy(null);
    }
  }

  const Wrap = card ? SignUpCard : Screen;
  return (
    <Wrap>
      {/*
        What they came for, first: an ad's visitor arrives here from a sentence they typed, and a
        bare "Create account" form gave them no reason to fill it in. Kept short enough that the
        button and its terms stay above the consent banner at 390x664 (see .auth-hero in
        global.css, and e2e-fold/signUpFold.spec.ts, which measures it).

        With an idea, their sentence is the example: the drawing is a wedding site whatever they
        typed, and its 132px set the height of the whole pitch.
      */}
      {idea && <IdeaQuote idea={idea} />}
      <div className={idea ? 'auth-hero auth-hero-text' : 'auth-hero'}>
        <div>
          {/*
            One line at 390px: "…to plan and build it" took two, and the planning is the sentence
            under it. Those 26px are what keep Create account above the banner at 390x664.
          */}
          <h1 className="type-title auth-headline">
            {card ? 'Create your account to finish your app' : idea ? 'Create an account to build it' : 'Create your account'}
          </h1>
          <p className="type-caption auth-sub">
            {card
              ? 'Your app keeps getting ready while you do this. Next you pick its features and its look.'
              : idea
                ? 'Next you choose its features and look, and Saddle builds it as a mobile website.'
                : 'Describe an app in a sentence, and Saddle builds it as a mobile website you can share.'}
          </p>
        </div>
        {!idea && !card && <ExampleSite />}
      </div>
      {(showApple || showGoogle) && (
        <div className="social-buttons">
          {showApple && (
            <button type="button" className="social-button social-apple" onClick={() => void continueWith('apple')} disabled={busy !== null}>
              <AppleMark />
              {busy === 'apple' ? 'Opening Apple…' : 'Continue with Apple'}
            </button>
          )}
          {showGoogle && (
            <button type="button" className="social-button social-google" onClick={() => void continueWith('google')} disabled={busy !== null}>
              <GoogleMark />
              {busy === 'google' ? 'Opening Google…' : 'Continue with Google'}
            </button>
          )}
          <p className="social-or type-caption" aria-hidden="true"><span>or</span></p>
        </div>
      )}
      <form onSubmit={submit} noValidate>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChangeText={setEmail}
          onBlur={() => setEmailLeft(true)}
          placeholder="you@example.com"
          error={emailLeft && email.trim() && !emailOk ? 'Enter your full email, like you@example.com' : undefined}
        />
        {error && <p role="alert" data-error-code="signup" className="type-caption field-error-text">{error}</p>}
        {/*
          Directly above the button it describes, with nothing between: that is what makes it
          agreement. Above rather than under, because what hides the bottom of a phone screen —
          the consent banner at 390x664, the on-screen keyboard — hid a line under the button
          while the button stayed in reach. Whatever shows the button shows this.
        */}
        <p className="type-caption auth-terms">
          By continuing you agree to the{' '}
          <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a> and{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
        </p>
        <Button title="Continue with email" type="submit" loading={busy === 'email'} disabled={!emailOk || (busy !== null && busy !== 'email')} />
        <p className="type-caption auth-sub">We’ll email you a 6-digit code. No password needed.</p>
      </form>
      {inApp !== 'none' && <OpenInBrowser />}
      {googleHidden && (
        <p className="type-caption auth-links social-hint">
          Google sign-in doesn’t work inside {IN_APP_NAME[inApp as keyof typeof IN_APP_NAME] ?? 'this app'}’s browser
          {showApple ? '. Use Apple or your email here' : '. Use your email here'}, or open this page in Safari or Chrome.
        </p>
      )}
      <p className="type-body auth-links">
        Already have an account? <Link to="/sign-in" state={from ? { from } : undefined}>Sign in</Link>
      </p>
      {/* Stuck here is a problem worth hearing about; reporting needs no account. */}
      <p className="type-caption auth-links"><ReportProblem label="Trouble signing up? Report a problem" /></p>
    </Wrap>
  );
}

/** The sign-up as a card over the loading view (see `card`). */
function SignUpCard({ children }: { children: ReactNode }) {
  return <section className="card clay-card signup-card" aria-label="Create your account">{children}</section>;
}

/*
 * The providers' marks, drawn here: a font glyph for Apple's logo shows only on Apple's own
 * devices, and an image would be one more request on the page that has to be fastest.
 */
function AppleMark() {
  return (
    <svg className="social-mark" aria-hidden="true" width="18" height="22" viewBox="0 0 814 1000" fill="currentColor">
      <path d="M788 341c-6 4-108 62-108 190 0 148 130 200 134 202-1 3-21 72-69 142-43 62-88 124-156 124s-86-40-164-40c-77 0-104 41-167 41s-106-57-156-128C44 789 0 668 0 553c0-184 120-282 238-282 63 0 115 41 155 41 38 0 97-44 168-44 27 0 125 2 189 94zM554 169c29-35 50-83 50-131 0-7-1-14-2-19-48 2-104 32-138 72-27 30-52 78-52 127 0 7 1 15 2 17 3 1 8 1 13 1 43 0 97-29 127-67z" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg className="social-mark" aria-hidden="true" width="20" height="20" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.8c4.3-4 6.9-9.9 6.9-17.1z" />
      <path fill="#FBBC05" d="M10.5 28.7c-.5-1.4-.8-3-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.8c-2.1 1.4-4.8 2.3-8.5 2.3-6.3 0-11.6-4.1-13.5-9.7l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
    </svg>
  );
}

/**
 * The quiet way out of an app's own browser (handoff §4): the same page in Safari or Chrome, with
 * the idea in the address and this browser's visitor id, so the journey stays one line and the
 * idea is waiting there. Photos and a plan cannot travel that way; the idea is what matters most.
 */
function OpenInBrowser() {
  const os = deviceProps().os;
  const draft = useDraft.getState();
  const href = openInBrowserUrl({ os, idea: draft.description });
  if (!href) return null;
  const browser = os === 'ios' ? 'Safari' : 'Chrome';
  return (
    <p className="type-caption auth-links open-in-browser">
      Trouble in this browser?{' '}
      <a href={href} onClick={() => funnelStep('open_in_browser', {}, { now: true })}>Open in {browser}</a>
      <span className="visually-hidden">.</span>{' '}
      If nothing happens, tap ••• at the top and choose “Open in browser”.
    </p>
  );
}
